// src/pages/dashboard/Support.js
// Help Center landing page — role-aware categories with SmartSearch
// Route: /dashboard/settings/support (practitioners) and /help (seekers)

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { getCategoriesForRole, getArticlesForRole } from '../../data/helpCenterContent';
import SmartSearch from '../../components/shared/SmartSearch';
import SupportContactForm from '../../components/support/SupportContactForm';

const Support = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = localStorage.getItem('userRole') || 'practitioner';
  const isPractitioner = userRole === 'practitioner' || userRole === 'admin';
  const effectiveRole = isPractitioner ? 'practitioner' : 'seeker';

  const categories = getCategoriesForRole(effectiveRole);
  const searchArticles = getArticlesForRole(effectiveRole);

  // Determine base path for navigation
  const isHelpRoute = location.pathname === '/help';
  const basePath = isHelpRoute ? '/help' : '/dashboard/settings/support';

  const handleSearchResultClick = (article) => {
    navigate(`${basePath}/${article.categorySlug}?article=${article.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* Breadcrumb */}
        {isPractitioner && !isHelpRoute ? (
          <nav className="flex items-center gap-2 text-sm mb-8" aria-label="Breadcrumb">
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate('/dashboard/practitioner')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/practitioner'); }}
              className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            >
              Dashboard
            </span>
            <span className="text-gray-400">/</span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate('/dashboard/settings')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/settings'); }}
              className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            >
              Settings
            </span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium" aria-current="page">Help Center</span>
          </nav>
        ) : null}

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Help Center</h1>
          <p className="text-sm text-gray-600 max-w-xl mx-auto mb-7">
            {isPractitioner
              ? 'Find answers, guides, and support for your Divine Frameworks practice'
              : 'Find answers about booking sessions, payments, and your account'}
          </p>

          {/* SmartSearch */}
          <SmartSearch
            articles={searchArticles}
            onResultClick={handleSearchResultClick}
            placeholder={isPractitioner ? 'Search the Help Center...' : 'Search for help...'}
          />
        </div>

        {/* Category Grid */}
        {categories.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {categories.map((cat) => {
              const { Icon } = cat;
              const hasArticles = cat.articles.length > 0;
              return (
                <span
                  key={cat.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => hasArticles && navigate(`${basePath}/${cat.slug}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && hasArticles) navigate(`${basePath}/${cat.slug}`); }}
                  className={`group w-full text-left p-6 bg-white rounded-xl border border-gray-200 transition-all block ${
                    hasArticles
                      ? 'hover:border-purple-300 hover:shadow-md cursor-pointer'
                      : 'opacity-60 cursor-default'
                  }`}
                  style={{ userSelect: 'none' }}
                >
                  {/* Icon badge */}
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 ${cat.iconBg}`}>
                    <Icon className={`w-5 h-5 ${cat.iconColor}`} />
                  </div>

                  {/* Title row */}
                  <h3 className="font-semibold text-gray-900 mb-1 flex items-center justify-between">
                    {cat.title}
                    {hasArticles ? (
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors flex-shrink-0" />
                    ) : null}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">{cat.description}</p>

                  {/* Article count */}
                  <span className="text-xs text-gray-500">
                    {hasArticles
                      ? `${cat.articles.length} ${cat.articles.length === 1 ? 'article' : 'articles'}`
                      : 'Coming soon'}
                  </span>
                </span>
              );
            })}
          </div>
        ) : null}

        {/* Support Contact Form */}
        <SupportContactForm />
      </div>
    </div>
  );
};

export default Support;
